/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState } from 'react';

import { generateDeviceId } from '@1debit/device-intelligence';
import { gql, useApolloClient } from '@apollo/client';
import { datadogRum } from '@datadog/browser-rum';
import { useMachine } from '@xstate/react';
import { requestStateMachine } from 'machines/requestStateMachine';
import getConfig from 'next/config';
import { useRouter } from 'next/router';

import pollTransactionStatus from 'components/SubPages/ClaimInfo/pollTransactionStatus';
import useArkose from 'components/SubPages/ClaimInfo/useArkose';
import { useNuData } from 'components/SubPages/ClaimInfo/utils/nudata/useNudata';
import { ClaimType } from 'components/SubPages/Landing/useCreateLandingPageHook';

import { useGqlMutation } from 'hooks/useGQLMutation';
import { MachineContext, useMachineContext } from 'hooks/useMachineContext';
import { fetcher } from 'hooks/useSWR';
import { useUserContext } from 'hooks/useUserContext';

import { deviceIntelligenceEnvironment } from 'utils/device-intelligence/deviceIntelligenceUtils';
import {
  trackCTATapped,
  trackPayAnyoneReceiverError,
  trackReturnUserPageViewed,
} from 'utils/segment/trackingEvents';

export const PAY_ANYONE_CLAIM_SAVED_DEBIT_CARD = gql`
  mutation PayAnyoneClaimSavedDebitResponse(
    $input: ClaimPayAnyoneWithSavedDebitCardInput!
  ) {
    unauthenticated {
      p2p {
        claim_pay_anyone_with_saved_debit_card(input: $input) {
          __typename
          ... on PayAnyoneClaimError {
            error_id
            error_body
            error_header
          }
          ... on ClaimPayAnyoneResult {
            success
            token
          }
        }
      }
    }
  }
`;

type UseReturnUserLandingApi = {
  onChangeCard: () => void;
  onCtaClickChime: () => void;
  onCtaClickReuseDebit: (lastFour: string, externalCardId: string) => void;
  isLoading: boolean;
  errorMessage: string;
  dismissError: () => void;
};

function useReturnUserLanding(
  amount: string,
  senderId: number
): UseReturnUserLandingApi {
  const {
    publicRuntimeConfig: { socureDeviceSdkKey, deployEnvironment },
  } = getConfig();
  const apolloClient = useApolloClient();
  const router = useRouter();
  const [userData] = useUserContext();
  const [state, send] = useMachineContext(MachineContext);
  const [requestState, requestSend] = useMachine(requestStateMachine);
  const { context } = state;
  const { publicId, token } = context;
  const [deviceSessionId, setDeviceSessionId] = useState(null);
  const useGqlGetAndClaimPayAnyone =
    userData?.flipperFlags?.graphqlUsage?.useGqlGetAndClaimPayAnyone;
  const saved_debit_card_graphql = useGqlMutation(
    PAY_ANYONE_CLAIM_SAVED_DEBIT_CARD
  );

  const {
    execute,
    // loading: graphqlloading,
    // error: graphqlerror,
    // data: graphqlData,
  } = saved_debit_card_graphql || {};

  const { runArkose } = useArkose();

  const {
    beginBehaviouralMonitoring,
    stopBehaviouralMonitoring,
    getBehaviouralData,
    getSessionId,
  } = useNuData();

  beginBehaviouralMonitoring();

  useEffect(() => {
    trackReturnUserPageViewed(amount, publicId, senderId);
  }, [amount, publicId, senderId]);

  useEffect(() => {
    const deviceOptions = {
      publicKey: socureDeviceSdkKey,
      endpoint: 'https://transaction.chime.com/api/ingestion',
      userConsent: true,
      context: 'login',
    };

    if (!window.devicer) {
      logErrorWithToken(token, publicId, 'Socure devicer failed to load.');
      send('ERROR_PAGE');
    } else {
      try {
        window.devicer.run(deviceOptions, response => {
          if (!response) {
            logErrorWithToken(
              token,
              publicId,
              'Socure devicer did not respond.'
            );
            send('ERROR_PAGE');
            return;
          }

          if (!response.sessionId) {
            logErrorWithToken(
              token,
              publicId,
              'Socure devicer did not respond with a sessionId.',
              response
            );
            send('ERROR_PAGE');
            return;
          }

          setDeviceSessionId(response.sessionId);
        });
      } catch (e) {
        logErrorWithToken(token, publicId, 'Socure devicer.run call failed', e);
        send('ERROR_PAGE');
      }
    }
  }, [context, publicId, send, socureDeviceSdkKey, token, router]);

  const submitDebitClaim = async (lastFour: string, externalCardId: string) => {
    const postUrl = `${process.env.CONSUMER_HOST}/pf/claim/${token}`;
    const pollingUrl = `${process.env.CONSUMER_HOST}/pf/${token}.json`;
    const pollingFunction = result => result.status;

    let arkoseToken = '';
    try {
      arkoseToken = await runArkose();
    } catch (error) {
      datadogRum.addError(error, {
        token,
        publicId,
      });
    }

    stopBehaviouralMonitoring();
    const ndsBehaviouralData = getBehaviouralData();
    const ndsSessionId = getSessionId();
    const deviceId = generateDeviceId({
      env: deviceIntelligenceEnvironment(deployEnvironment),
    });

    try {
      requestSend('START');
      const pfPath = postUrl?.includes(`/pf/`);
      if (useGqlGetAndClaimPayAnyone && pfPath) {
        const result = await execute({
          input: {
            last_four: lastFour,
            external_card_id: externalCardId,
            device_id: deviceSessionId,
            arkose_token: arkoseToken,
            nds: ndsBehaviouralData,
            nudata_session_id: ndsSessionId,
            token,
          },
        });
        const { data: claimData, errors: claimError } = result || {};
        const claimDataResponse =
          claimData?.unauthenticated?.p2p
            ?.claim_pay_anyone_with_saved_debit_card;
        if (claimDataResponse?.__typename === 'PayAnyoneClaimError') {
          throw claimDataResponse;
        } else if (claimError) {
          throw claimError;
        }
      } else {
        await fetcher(postUrl, {
          method: 'POST',
          json: {
            last_four: lastFour,
            external_card_id: externalCardId,
            device_id: deviceSessionId,
            arkose_token: arkoseToken,
            nds: ndsBehaviouralData,
            nudata_session_id: ndsSessionId,
          },
        });
      }

      await pollTransactionStatus(pollingUrl, pollingFunction, {
        token,
        apolloClient,
        useGqlGetAndClaimPayAnyone,
      });

      requestSend('RESUME');
      send('NEXT');
    } catch (e) {
      if (e.error_body) {
        trackPayAnyoneReceiverError('Transfer', e.error_body, publicId);

        send('ERROR_PAGE', e as never);
        requestSend('RESUME');
        return;
      }

      if (e.error_header) {
        trackPayAnyoneReceiverError('Transfer', e.error_header, publicId);
        requestSend('ERROR', { errorMessage: e.error_header });
        return;
      }

      trackPayAnyoneReceiverError('Transfer', 'Something went wrong', publicId);

      send('ERROR_PAGE');
      requestSend('RESUME');
    }
  };

  const fireDefaultCtaButtonEvents = claimType => {
    trackClaimClick(publicId, claimType);
  };

  const onCtaClickChime = () => {
    fireDefaultCtaButtonEvents(ClaimType.CHIME_ENROLLMENT);
  };

  const onCtaClickReuseDebit = async (
    lastFour: string,
    externalCardId: string
  ) => {
    fireDefaultCtaButtonEvents(ClaimType.DEBIT_CARD);
    await submitDebitClaim(lastFour, externalCardId);
  };

  const onChangeCard = () => {
    trackOnChangeCardClick(publicId);
    send('CHANGE_CARD');
  };

  const dismissError = () => {
    requestSend('RESUME');
  };

  return {
    onChangeCard,
    onCtaClickChime,
    onCtaClickReuseDebit,
    isLoading: requestState.value === 'loading',
    errorMessage: requestState.context?.errorMessage,
    dismissError,
  };
}

const logErrorWithToken = (
  token: string,
  publicId: string,
  message: string,
  ...rest
) => {
  const error = new Error(message);
  window.Rollbar.error(
    message,
    {
      token,
      publicId,
    },
    ...rest
  );
  datadogRum.addError(error, {
    token,
    publicId,
    ...rest,
  });
};

const trackOnChangeCardClick = publicId =>
  trackCTATapped(
    'Pay Anyone',
    'Need to change debit card?',
    'Return User Page',
    'pay_anyone_change_debit_card_button',
    publicId,
    null
  );

const trackClaimClick = (publicId, claimType) =>
  trackCTATapped(
    'Pay Anyone',
    'Get $',
    'Return User Page',
    'get_money_button',
    publicId,
    claimType
  );

export default useReturnUserLanding;
